import { extractDigits } from 'shared/utilities/digits';
import { normalizeOptionalBoolean, normalizeSkills, uniqueSkills } from 'auth/userAdapter';
import { defaultCountryCode, employmentFieldsRequired, normalizeListedCode } from 'auth/profileOptions';

export const PROFILE_FIELD_ORDER = [
  'full_name',
  'age_range',
  'education_level',
  'is_employed',
  'work_experience',
  'experience_field',
  'skills',
  'country',
];

export function emptyProfileValues(overrides = {}) {
  return {
    full_name: overrides.full_name || '',
    age_range: overrides.age_range || '',
    education_level: overrides.education_level || '',
    is_employed: Object.prototype.hasOwnProperty.call(overrides, 'is_employed')
      ? normalizeOptionalBoolean(overrides.is_employed)
      : null,
    work_experience: overrides.work_experience || '',
    experience_field: overrides.experience_field || '',
    skills: Array.isArray(overrides.skills) ? uniqueSkills(overrides.skills) : [],
    country: overrides.country || '',
  };
}

export function valuesFromUser(user, options) {
  const country = normalizeListedCode(user?.country, options?.countries)
    || defaultCountryCode(options)
    || '';
  return emptyProfileValues({
    full_name: user?.full_name || user?.name || '',
    age_range: normalizeListedCode(user?.age_range, options?.age_ranges) || '',
    education_level: normalizeListedCode(user?.education_level, options?.education_levels) || '',
    is_employed: user?.is_employed,
    work_experience: normalizeListedCode(user?.work_experience, options?.work_experience) || '',
    experience_field: user?.experience_field || '',
    skills: user?.skills,
    country,
  });
}

export function valuesFromDraft(draft, options) {
  return valuesFromUser({
    full_name: draft?.full_name || draft?.draft_full_name || '',
    age_range: draft?.age_range,
    education_level: draft?.education_level,
    is_employed: draft?.is_employed,
    work_experience: draft?.work_experience,
    experience_field: draft?.experience_field,
    skills: draft?.skills,
    country: draft?.country,
  }, options);
}

export function addSkillValue(skills, raw, limits) {
  const maxItems = limits?.max_items || 20;
  const maxLength = limits?.max_length || 80;
  const next = uniqueSkills(skills);
  String(raw || '').split(/[,،]/).forEach((part) => {
    const skill = part.trim();
    if (!skill || next.length >= maxItems) {
      return;
    }
    const clipped = skill.slice(0, maxLength);
    if (!clipped) {
      return;
    }
    if (next.some((item) => item.toLocaleLowerCase() === clipped.toLocaleLowerCase())) {
      return;
    }
    next.push(clipped);
  });
  return next;
}

export function removeSkillValue(skills, index) {
  return (Array.isArray(skills) ? skills : []).filter((_, current) => current !== index);
}

export function validateProfileValues(values, options, t, { mode = 'onboarding' } = {}) {
  const errors = {};
  const limits = options?.limits || {};
  const name = String(values.full_name || '').trim();
  const minName = limits.full_name?.min || 2;
  const maxName = limits.full_name?.max || 150;
  if (name.length < minName || name.length > maxName) {
    errors.full_name = t('profile.invalid');
  }
  if (!normalizeListedCode(values.age_range, options?.age_ranges)) {
    errors.age_range = t('profile.required');
  }
  if (!normalizeListedCode(values.education_level, options?.education_levels)) {
    errors.education_level = t('profile.required');
  }
  if (values.is_employed !== true && values.is_employed !== false) {
    errors.is_employed = t('profile.required');
  }
  if (employmentFieldsRequired(values.is_employed, options)) {
    if (!normalizeListedCode(values.work_experience, options?.work_experience)) {
      errors.work_experience = t('profile.required');
    }
    const field = String(values.experience_field || '').trim();
    const maxField = limits.experience_field?.max || 150;
    if (!field || field.length > maxField) {
      errors.experience_field = field ? t('profile.invalid') : t('profile.required');
    }
  }
  const skills = normalizeSkills(values.skills);
  const maxItems = limits.skills?.max_items || 20;
  const maxLength = limits.skills?.max_length || 80;
  const minItems = mode === 'edit'
    ? 0
    : (limits.skills?.min_items ?? 1);
  if (skills.length < minItems || skills.length > maxItems || skills.some((item) => item.length > maxLength)) {
    errors.skills = skills.length < minItems ? t('profile.required') : t('profile.invalid');
  }
  if (!normalizeListedCode(values.country, options?.countries)) {
    errors.country = t('profile.required');
  }
  return errors;
}

export function firstInvalidField(errors) {
  return PROFILE_FIELD_ORDER.find((key) => errors?.[key]) || Object.keys(errors || {})[0] || null;
}

export function focusField(fieldId) {
  if (!fieldId || typeof document === 'undefined') {
    return;
  }
  const node = document.getElementById(fieldId === 'is_employed' ? 'is_employed-true' : fieldId);
  if (node && typeof node.focus === 'function') {
    node.focus();
  }
}

export function buildProfileCompletionFields(values, options) {
  const employed = values.is_employed === true;
  const payload = {
    full_name: String(values.full_name || '').trim(),
    age_range: values.age_range,
    education_level: values.education_level,
    is_employed: employed,
    skills: normalizeSkills(values.skills),
    country: values.country,
  };
  if (employmentFieldsRequired(employed, options)) {
    payload.work_experience = values.work_experience;
    payload.experience_field = String(values.experience_field || '').trim();
  }
  return payload;
}

export function buildProfileCompletionPayload({
  phone,
  code,
  values,
  locale,
  acceptTerms,
  acceptPrivacy,
  terms,
  privacy,
  options,
}) {
  const payload = {
    phone,
    code: extractDigits(code, 6),
    ...buildProfileCompletionFields(values, options),
    preferred_locale: locale,
    device_type: 'web',
  };
  if (acceptTerms && terms?.id) {
    payload.terms_document_id = terms.id;
    if (terms.version) {
      payload.terms_version = terms.version;
    }
  }
  if (acceptPrivacy && privacy?.id) {
    payload.privacy_document_id = privacy.id;
    if (privacy.version) {
      payload.privacy_version = privacy.version;
    }
  }
  return payload;
}

function sameSkillList(left, right) {
  return JSON.stringify(normalizeSkills(left)) === JSON.stringify(normalizeSkills(right));
}

export function buildProfilePatch(values, original, options) {
  const next = buildProfileCompletionFields(values, options);
  const previous = {
    full_name: String(original?.full_name || original?.name || '').trim(),
    age_range: original?.age_range || null,
    education_level: original?.education_level || null,
    is_employed: original?.is_employed,
    work_experience: original?.work_experience || null,
    experience_field: original?.experience_field || null,
    skills: normalizeSkills(original?.skills),
    country: original?.country || null,
  };
  const patch = {};
  ['full_name', 'age_range', 'education_level', 'country'].forEach((key) => {
    if ((next[key] || null) !== (previous[key] || null)) {
      patch[key] = next[key];
    }
  });
  if (next.is_employed !== previous.is_employed) {
    patch.is_employed = next.is_employed;
  }
  if (next.is_employed === true) {
    if ((next.work_experience || null) !== previous.work_experience) {
      patch.work_experience = next.work_experience;
    }
    if ((next.experience_field || null) !== previous.experience_field) {
      patch.experience_field = next.experience_field;
    }
  }
  if (!sameSkillList(next.skills, previous.skills)) {
    patch.skills = next.skills;
  }
  return patch;
}
